import { useEffect, useState } from "react";
import billingService from "../../services/billingService";
import {
  Paper,
  Box,
  Typography,
  Chip,
  LinearProgress,
  Button,
  Skeleton,
} from "@mui/material";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import { Link as RouterLink } from "react-router-dom";
import usageService from "../../services/usageService";
import userPlanService from "../../services/userPlanService";

const formatDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime())
    ? null
    : d.toLocaleDateString("es-AR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
};

// Vigencia según la fecha de renovación (mismo criterio que PlanBanner).
const evaluarVigencia = (expiry) => {
  if (!expiry) return null;
  const d = new Date(expiry);
  if (isNaN(d.getTime())) return null;
  const days = Math.ceil((d - new Date()) / (24 * 60 * 60 * 1000));
  if (days < 0) return { label: "Vencido", color: "error" };
  if (days <= 7) return { label: "Por vencer", color: "warning" };
  return { label: "Vigente", color: "success" };
};

// Card "Mi plan": plan vigente + consumo de egresos del ciclo. El uso viene del
// endpoint del backend (movement/usage), que es la fuente de verdad (cupo + conteo).
const MiPlanCard = () => {
  const [usage, setUsage] = useState(null);
  const [planItem, setPlanItem] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const userId = Number(sessionStorage.getItem("User"));
    if (!userId) {
      setLoading(false);
      return;
    }
    Promise.allSettled([
      usageService.getEgresoUsage(userId),
      userPlanService.getUserPlans(userId),
    ])
      .then(([u, p]) => {
        if (u.status === "fulfilled") setUsage(u.value);
        if (p.status === "fulfilled") setPlanItem(p.value?.items?.[0] ?? null);
      })
      .finally(() => setLoading(false));
  }, []);

  const planName = usage?.plan || planItem?.plan || "—";
  const expiry = planItem?.expiry_date ?? planItem?.expiryDate;
  const vigencia = evaluarVigencia(expiry);
  const status = usage?.status;
  const unlimited = status === "unlimited";
  const hasLimit = usage && usage.limit != null;
  const used = usage?.used ?? 0;
  const limit = usage?.limit ?? 0;
  const remaining = usage?.remaining ?? 0;
  const pct = hasLimit && limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const barColor =
    status === "blocked" ? "error" : status === "warning" ? "warning" : "primary";

  return (
    <Paper
      elevation={0}
      sx={{ borderRadius: 3, border: "1px solid #e0e0e0", overflow: "hidden" }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "grey.50",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CreditCardOutlinedIcon color="primary" fontSize="small" />
          <Typography variant="subtitle1" fontWeight={600}>
            Mi plan
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          onClick={async () => {
            // Suscripción por Stripe → Customer Portal (cambiar plan / cancelar /
            // facturas). Sin Stripe (plan manual o no configurado) → página de planes.
            try {
              const url = await billingService.portal();
              if (url) {
                window.location.href = url;
                return;
              }
            } catch (error) {
              /* fallback abajo */
            }
            window.location.href = "/plans";
          }}
          sx={{ textTransform: "none" }}
        >
          Cambiar / Renovar
        </Button>
      </Box>

      <Box sx={{ p: 3 }}>
        {loading ? (
          <Skeleton variant="rounded" height={72} />
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
                mb: 2,
              }}
            >
              <Typography variant="h6" fontWeight={700}>
                {planName}
              </Typography>
              {vigencia && (
                <Chip size="small" label={vigencia.label} color={vigencia.color} variant="outlined" />
              )}
              {expiry && (
                <Typography variant="body2" color="text.secondary">
                  {vigencia?.label === "Vencido" ? "Venció" : "Vence"} el {formatDate(expiry)}
                </Typography>
              )}
            </Box>

            {unlimited ? (
              <Typography variant="body2" color="text.secondary">
                Egresos ilimitados.
              </Typography>
            ) : hasLimit ? (
              <Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Egresos despachados este ciclo
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {used} / {limit}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={pct}
                  color={barColor}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.75, display: "block" }}
                >
                  {status === "blocked"
                    ? "Alcanzaste el límite de egresos de tu plan."
                    : `Te quedan ${remaining} egresos · el cupo se reinicia cada mes en tu fecha de renovación.`}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Sin cupo de egresos configurado para este plan.
              </Typography>
            )}
          </>
        )}
      </Box>
    </Paper>
  );
};

export default MiPlanCard;
